import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { HttpService } from '../http/http.service';
import { environment } from '../../../environments/environment';
import {
  PortalInviteAccept,
  PortalInvitePreview,
  PortalSupplier,
} from '../../models/portal/portal-supplier.model';
import { PortalCatalog } from '../../models/portal/portal-catalog.model';
import {
  PurchaseOrder,
  PurchaseOrderCompact,
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
} from '../../models/portal/purchase-order.model';
import { PortalOrdersPageCriteria } from '../../models/portal/purchase-orders-page-criteria.model';
import { PaginatedList } from '../../models/pagination/paginated-list.model';

const portalSuppliersCacheBuster$ = new Subject<void>();
const portalOrdersCacheBuster$ = new Subject<void>();

/** Buyer side of the customer portal (the producer side lives in CustomerService). */
@Injectable({ providedIn: 'root' })
export class PortalService {
  private readonly http = inject(HttpService);

  /** Producers the current organization is linked to (any status). */
  @Cacheable({ cacheBusterObserver: portalSuppliersCacheBuster$, maxAge: environment.maxCacheAge })
  getSuppliers(): Observable<PortalSupplier[]> {
    return this.http.get<PortalSupplier[]>('customer-portal/suppliers');
  }

  /** Public, token-scoped preview for the accept page (no auth required). */
  previewInvitation(token: string): Observable<PortalInvitePreview> {
    return this.http.get<PortalInvitePreview>(
      'customer-portal/invitation/preview',
      { params: { token } },
      false,
    );
  }

  /** Accepts the invitation as the authenticated, verified user. */
  @CacheBuster({ cacheBusterNotifier: portalSuppliersCacheBuster$ })
  acceptInvitation(token: string): Observable<PortalInviteAccept> {
    return this.http.post<PortalInviteAccept>(
      'customer-portal/invitation/accept',
      { token },
      undefined,
      false,
    );
  }

  /** Sanitized producer catalog (no prices/costs/hidden fields). */
  @Cacheable({ cacheBusterObserver: portalOrdersCacheBuster$, maxAge: environment.maxCacheAge })
  getCatalog(producerOrganizationId: string): Observable<PortalCatalog> {
    return this.http.get<PortalCatalog>(
      `customer-portal/suppliers/${producerOrganizationId}/catalog`,
    );
  }

  @Cacheable({ cacheBusterObserver: portalOrdersCacheBuster$, maxAge: environment.maxCacheAge })
  getOrders(criteria: PortalOrdersPageCriteria): Observable<PaginatedList<PurchaseOrderCompact>> {
    return this.http.get<PaginatedList<PurchaseOrderCompact>>('customer-portal/orders', {
      params: criteria,
    });
  }

  @Cacheable({ cacheBusterObserver: portalOrdersCacheBuster$, maxAge: environment.maxCacheAge })
  getOrder(orderId: string): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`customer-portal/orders/${orderId}`);
  }

  @CacheBuster({ cacheBusterNotifier: portalOrdersCacheBuster$ })
  createOrder(data: PurchaseOrderCreate): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>('customer-portal/orders', data);
  }

  /** Full in-place overwrite of the spec - allowed until the order is quoted. */
  @CacheBuster({ cacheBusterNotifier: portalOrdersCacheBuster$ })
  updateOrder(orderId: string, data: PurchaseOrderUpdate): Observable<PurchaseOrder> {
    return this.http.put<PurchaseOrder>(`customer-portal/orders/${orderId}`, data);
  }

  @CacheBuster({ cacheBusterNotifier: portalOrdersCacheBuster$ })
  submitOrder(orderId: string): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>(`customer-portal/orders/${orderId}/submit`);
  }

  @CacheBuster({ cacheBusterNotifier: portalOrdersCacheBuster$ })
  cancelOrder(orderId: string): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>(`customer-portal/orders/${orderId}/cancel`);
  }

  /** Accept the quote (price AND proposed delivery date) - converts the order
   * into a Sale at the producer. */
  @CacheBuster({ cacheBusterNotifier: portalOrdersCacheBuster$ })
  acceptOrder(orderId: string): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>(`customer-portal/orders/${orderId}/accept`);
  }
}
